import React from "react";

/**
 * MobileTabBar — sticky horizontal tab strip that organizes a long mobile
 * page into bite-sized panels. Hidden entirely on `lg+` so the desktop
 * layout keeps all sections visible at once.
 *
 * Usage:
 *   const [tab, setTab] = useState('broadcast');
 *   <MobileTabBar tab={tab} setTab={setTab} tabs={[
 *     { id: 'broadcast', label: 'Broadcast', icon: Video },
 *     { id: 'audio',     label: 'Audio',     icon: Sliders },
 *     ...
 *   ]} />
 *   <MobileTabSection tab={tab} match="broadcast">{...content...}</MobileTabSection>
 *
 * MobileTabSection is just a `lg:block` wrapper that hides on mobile when
 * `tab !== match`. Sections always show on lg+ so the desktop layout is
 * untouched.
 */
export function MobileTabBar({ tab, setTab, tabs, testid = "mobile-tab-bar" }) {
  if (!tabs || tabs.length === 0) return null;
  return (
    <div
      data-testid={testid}
      // Sticky right under the page's sticky header so the tab bar always
      // floats above the active panel as the user scrolls.
      className="lg:hidden sticky top-[120px] z-20 bg-black/85 backdrop-blur-md border-b border-white/10 -mx-3 sm:-mx-6 px-3 sm:px-6 py-1.5 mb-3"
    >
      <div className="flex gap-1 overflow-x-auto no-scrollbar">
        {tabs.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              data-testid={`${testid}-${t.id}`}
              className={`shrink-0 px-3 py-1.5 rounded-full text-[11px] font-black uppercase tracking-wider inline-flex items-center gap-1.5 border transition ${
                active
                  ? "bg-tv-accent text-black border-tv-accent shadow-[0_0_20px_rgba(255,212,0,0.3)]"
                  : "bg-white/5 text-white/70 border-white/10 hover:bg-white/10"
              }`}
              aria-pressed={active}
            >
              {Icon && <Icon className="w-3.5 h-3.5" />}
              {t.label}
              {t.badge != null && t.badge !== 0 && (
                <span className={`ml-0.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[9px] font-black ${
                  active ? "bg-black/20 text-black" : "bg-tv-accent text-black"
                }`}>{t.badge}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * MobileTabSection — wraps a chunk of UI so it only shows on mobile when
 * the active tab matches `match`. On `lg+` it always shows (so desktop
 * users still see every section side-by-side).
 */
export function MobileTabSection({ tab, match, children, className = "" }) {
  // Multiple matches OK ("broadcast,audio") — splits and trims.
  const matches = (Array.isArray(match) ? match : String(match).split(","))
    .map((s) => s.trim())
    .filter(Boolean);
  const visibleOnMobile = matches.includes(tab);
  return (
    <div className={`${visibleOnMobile ? "block" : "hidden"} lg:block ${className}`}>
      {children}
    </div>
  );
}

/**
 * MobileBottomNav — companion to MobileTabBar that pins the same tabs
 * to the bottom of the viewport on mobile. Mirrors TikTok / IG Live /
 * Twitch mobile UX where the active section can be switched with a
 * single thumb-tap without scrolling back up to a sticky top bar.
 *
 * Reuses the same `tabs / tab / setTab` props so a page only manages
 * one source of truth for which tab is active. Hidden on `lg+`.
 *
 * Visual: rounded pill above the safe-area inset, glass surface,
 * active tab gets the tv-accent fill so it's instantly findable.
 */
export function MobileBottomNav({ tab, setTab, tabs, testid = "mobile-bottom-nav", quickActions = null }) {
  // Quick-action sheet — reveals on tap of the grab handle OR a swipe-up
  // gesture anywhere on the nav. Gives thumb-reach access to the
  // live-broadcast shortcut actions (mic, flip, GS, FX, end) without
  // permanently occupying a second nav row. Matches IG/TikTok Live's
  // "handle-bar reveals quick actions" pattern. Hooks MUST live at the
  // top of the component (never after an early return) or React will
  // throw "rendered fewer hooks than expected" on re-renders.
  const [sheetOpen, setSheetOpen] = React.useState(false);
  const touchStartRef = React.useRef(null);

  if (!tabs || tabs.length === 0) return null;

  const onTouchStart = (e) => {
    if (!quickActions?.length) return;
    touchStartRef.current = e.touches?.[0]?.clientY ?? null;
  };
  const onTouchEnd = (e) => {
    if (!quickActions?.length || touchStartRef.current == null) return;
    const endY = e.changedTouches?.[0]?.clientY ?? touchStartRef.current;
    const dy = touchStartRef.current - endY; // positive = swiped up
    touchStartRef.current = null;
    // Threshold: ≥30px upward gesture opens the sheet. Anything less
    // is treated as a regular tab tap.
    if (dy > 30) setSheetOpen(true);
  };

  return (
    <>
      {/* Quick-action sheet — fades backdrop + slides content from
          behind the nav. Only mounts when quickActions were provided. */}
      {quickActions?.length > 0 && sheetOpen && (
        <div
          data-testid={`${testid}-quick-sheet-backdrop`}
          className="lg:hidden fixed inset-0 z-[44] bg-black/50 backdrop-blur-[2px]"
          onClick={() => setSheetOpen(false)}
        />
      )}
      {quickActions?.length > 0 && (
        <div
          data-testid={`${testid}-quick-sheet`}
          aria-hidden={!sheetOpen}
          className={`lg:hidden fixed left-0 right-0 z-[45] px-3 pointer-events-none transition-transform duration-300 ease-out`}
          style={{
            bottom: "calc(env(safe-area-inset-bottom, 0) + 76px)",
            transform: sheetOpen ? "translateY(0)" : "translateY(120%)",
          }}
        >
          <div className="pointer-events-auto mx-auto max-w-md rounded-2xl bg-black/90 backdrop-blur-xl border border-white/10 shadow-[0_-8px_32px_rgba(0,0,0,0.5)] p-3 space-y-2">
            <div className="text-[9px] uppercase tracking-wider text-white/40 font-black">
              Quick actions
            </div>
            <div className="grid grid-cols-4 gap-2">
              {quickActions.map((a) => {
                const Icon = a.icon;
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => {
                      a.onClick?.();
                      if (!a.keepOpen) setSheetOpen(false);
                    }}
                    data-testid={`${testid}-quick-${a.id}`}
                    aria-pressed={a.active}
                    className={`flex flex-col items-center justify-center gap-1 py-3 rounded-xl transition-all duration-200 active:scale-95 ${
                      a.active
                        ? a.danger
                          ? "bg-tv-live/90 text-white"
                          : "bg-tv-accent text-black"
                        : "bg-white/5 text-white/85 hover:bg-white/10"
                    }`}
                  >
                    {Icon && <Icon className="w-5 h-5" />}
                    <span className="text-[10px] font-black uppercase tracking-wider leading-none">{a.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div
        data-testid={testid}
        className="lg:hidden fixed bottom-0 left-0 right-0 z-40 px-2 pb-[calc(env(safe-area-inset-bottom,0)+8px)] pt-2 pointer-events-none"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {/* Grab handle — only visible when quickActions are available.
            Tap to toggle the quick-action sheet. Subtle pill so it
            doesn't steal focus from the main nav. */}
        {quickActions?.length > 0 && (
          <button
            type="button"
            onClick={() => setSheetOpen((v) => !v)}
            data-testid={`${testid}-grab-handle`}
            aria-label="Quick actions"
            aria-expanded={sheetOpen}
            className="pointer-events-auto mx-auto mb-1 block w-10 h-1 rounded-full bg-white/40 hover:bg-white/70 active:bg-tv-accent transition-colors"
          />
        )}
        <div
          className="pointer-events-auto mx-auto max-w-md rounded-2xl bg-black/70 backdrop-blur-xl border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.5)] px-1 py-1 flex items-center justify-between gap-0.5"
        >
          {tabs.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => {
                  setTab(t.id);
                  // Scroll back to the top of the section so the user lands
                  // at the start of the new tab (not somewhere in the middle
                  // of where they were on the previous tab).
                  if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                data-testid={`${testid}-${t.id}`}
                aria-pressed={active}
                aria-label={t.label}
                className={`flex-1 flex flex-col items-center justify-center gap-0.5 px-1 py-1.5 rounded-xl transition-all duration-200 active:scale-95 relative ${
                  active
                    ? "bg-tv-accent text-black"
                    : "text-white/65 hover:text-white hover:-translate-y-0.5"
                }`}
              >
                {Icon && <Icon className="w-4 h-4 shrink-0" />}
                <span className="text-[9px] font-black uppercase tracking-wider leading-none">{t.label}</span>
                {t.badge != null && t.badge !== 0 && (
                  <span className={`absolute top-0.5 right-1 inline-flex items-center justify-center min-w-[14px] h-3.5 px-1 rounded-full text-[8px] font-black ${
                    active ? "bg-black/25 text-black" : "bg-tv-accent text-black"
                  }`}>{t.badge}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}

